import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Link from '../Link';
import IconBadge from '../IconBadge';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import useExperiment from '../Experiments/useExperiment';
import { RESY_WEB_ENABLE_2025_CODE_FREEZE_BUG_175 } from '../../constants/optimizelyExperiments';

import './CollectionHeadline.scss';

const CollectionHeadline = ({
  badge,
  component,
  title,
  translationKey,
  description,
  eyebrow,
  link,
  handleSeeMoreClick,
  isGda,
  isEvents,
  isMobile,
  isPlatinum,
  showPlatinumSeeAllLink,
}) => {
  const [isCodeFreezeFlagOn] = useExperiment(RESY_WEB_ENABLE_2025_CODE_FREEZE_BUG_175);

  const renderBadge = () => {
    const platinumComponentSize = isMobile ? '66' : '130';
    const nonPlatinumComponentSize = isGda ? '20' : '34';

    return (
      <div
        className={cx('CollectionHeadline__badge', {
          'CollectionHeadline__badge--platinum': isPlatinum,
          'CollectionHeadline__badge--mobile': isMobile,
        })}
      >
        {badge && (
          <IconBadge
            name="collection"
            iconClass={isPlatinum ? 'platinum' : ''}
            url={badge}
            size={isPlatinum ? platinumComponentSize : nonPlatinumComponentSize}
            bgToSvgRatio="1"
          />
        )}
        {component && (
          <IconBadge
            name="collection"
            component={component}
            size="34"
            bgToSvgRatio="1"
          />
        )}
      </div>
    );
  };

  const renderTitle = () => (
    <span>
      {translationKey ? (
        <TranslationProvider translationKey={translationKey}>
          <span>
            {title}
          </span>
        </TranslationProvider>
      ) : (
        <span>
          {title}
        </span>
      )}
    </span>
  );

  const renderDescription = () => {
    if (isPlatinum) {
      return (
        <div
          className="CollectionHeadline__description"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: description,
          }}
        />
      );
    }

    return (
      <div className="CollectionHeadline__description">
        {description}
      </div>
    );
  };

  const isPlatinumUserOnMobile = isPlatinum && isMobile;
  return (
    <>
      <div className={cx(
        'CollectionHeadline', {
          CollectionHeadline__RGA: isGda,
          CollectionHeadline__events: isEvents,
          CollectionHeadline__mobile: isMobile,
          CollectionHeadline__full: isPlatinum && showPlatinumSeeAllLink,
        }
      )}
      >
        {(!isMobile && !isGda) && (badge || component) && renderBadge()}
        <div className="CollectionHeadline__content">
          {isPlatinumUserOnMobile && renderBadge()}

          <div className={cx('CollectionHeadline__content--col-fill', {
            'CollectionHeadline__content--col-fill--platinum': isPlatinum,
          })}
          >
            <div className="CollectionHeadline__badgeText">
              {(isGda && !isPlatinum) && renderBadge()}
              {eyebrow && (
                <div className={cx('CollectionHeadline__eyebrow', {
                  'CollectionHeadline__eyebrow--platinum': isPlatinum,
                  'CollectionHeadline__eyebrow--platinum--mobile': isMobile,
                })}
                >
                  {eyebrow}
                </div>
              )}
            </div>
            {link ? (
              <>
                <h2 className={cx('CollectionHeadline__title CollectionHeadline__title--desktop', {
                  'CollectionHeadline__title--desktop--platinum': isMobile,
                })}
                >
                  {renderTitle()}
                </h2>
                <h2 className={cx('CollectionHeadline__title CollectionHeadline__title--mobile', {
                  'CollectionHeadline__title--mobile--platinum': isPlatinum,
                })}
                >
                  <Link
                    {...(isEvents ? { href: link } : {})}
                    onClick={handleSeeMoreClick}
                  >
                    {renderTitle()}
                  </Link>
                </h2>
              </>
            ) : (
              <h2 className={cx('CollectionHeadline__title', {
                'CollectionHeadline__title--mobile--platinum': isPlatinum && isMobile,
              })}
              >
                {renderTitle()}
              </h2>
            )}
            {description && !isPlatinumUserOnMobile && (
              renderDescription()
            )}
          </div>
          {((!isMobile && link) || (showPlatinumSeeAllLink)) && (
            <div
              className={cx('CollectionHeadline__link', {
                'CollectionHeadline__link--platinum': isPlatinum,
              })}
            >
              <Link
                {...(isEvents || isPlatinum ? { href: link } : {})}
                onClick={handleSeeMoreClick}
                {...(isPlatinum && isCodeFreezeFlagOn ? { target: '_blank' } : {})}
                aria-label={getTranslation({
                  translationKey: 'see_all_collection',
                  translationValues: { title },
                  fallback: `See All ${title}`,
                })}
              >
                <TranslationProvider translationKey="see_all">
                  <span>See All</span>
                </TranslationProvider>
              </Link>
            </div>
          )}
        </div>
      </div>

      {description && isPlatinumUserOnMobile && (
        <div
          className="CollectionHeadline__description CollectionHeadline__description--platinum-mobile"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: description,
          }}
        />
      )}
    </>
  );
};

CollectionHeadline.propTypes = {
  badge: PropTypes.string,
  component: PropTypes.string,
  description: PropTypes.shape({}),
  eyebrow: PropTypes.string,
  handleSeeMoreClick: PropTypes.func,
  showPlatinumSeeAllLink: PropTypes.bool,
  isGda: PropTypes.bool,
  isEvents: PropTypes.bool,
  isMobile: PropTypes.bool,
  isPlatinum: PropTypes.bool,
  link: PropTypes.string,
  title: PropTypes.string,
  translationKey: PropTypes.string,
};

CollectionHeadline.defaultProps = {
  badge: undefined,
  component: undefined,
  description: undefined,
  eyebrow: '',
  handleSeeMoreClick: () => { },
  showPlatinumSeeAllLink: false,
  isGda: false,
  isEvents: false,
  isMobile: false,
  isPlatinum: false,
  link: '',
  title: '',
  translationKey: '',
};

CollectionHeadline.displayName = 'CollectionHeadline';
export default CollectionHeadline;
